import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../config/api";

interface Script {
  id: string;
  scriptId: string;
  name: string;
  websiteUrl: string;
  customization: any;
  isActive: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  scriptCode?: string;
  industryCategory?: string;
}

interface CreateScriptData {
  name: string;
  websiteUrl: string;
  selectedCategory?: string;
  customization: {
    position: string;
    theme: string;
    primaryColor: string;
    welcomeMessage: string;
    title: string;
    placeholder: string;
    maxWidth: number;
    maxHeight: number;
    autoOpen: boolean;
    showOnMobile: boolean;
  };
}

interface UserInfo {
  category?: string;
  name?: string;
}

interface AvailableCategory {
  category_id: string;
  category_name: string;
  description: string;
  defaultWelcome: string;
  placeholder: string;
  allowedTopics: string[];
  tone: string;
  style: string;
}

const ScriptGenerator: React.FC = () => {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [availableCategories, setAvailableCategories] = useState<AvailableCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [actionLoading, setActionLoading] = useState<{
    type: string;
    id: string;
  } | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo>({});

  // Get current selected category configuration
  const getSelectedCategoryConfig = (categoryId?: string) => {
    const selectedCategory = availableCategories.find(
      cat => cat.category_id === (categoryId || userInfo.category)
    );
    
    return selectedCategory || {
      category_id: "GENERAL",
      category_name: "General",
      description: "General purpose AI assistant suitable for any website",
      defaultWelcome: "Hi! I'm your AI assistant! How can I help you today?",
      placeholder: "Type your message...",
      allowedTopics: [],
      tone: "professional-friendly",
      style: "helpful-general"
    };
  };

  // Dynamic icon mapping
  const getCategoryIcon = (categoryId: string) => {
    const iconMap: {[key: string]: string} = {
      'ECOMMERCE': '🛍️',
      'REAL_ESTATE': '🏠',
      'ERP': '📋',
      'IP_REGISTRATION': '📝',
      'HEALTHCARE': '🏥',
      'FINANCE': '💰',
      'EDUCATION': '🎓',
      'LEGAL': '⚖️',
      'TRAVEL': '✈️',
      'FOOD': '🍕',
      'TECH': '💻',
      'RETAIL': '🛒',
      'CONSULTING': '💼',
      'AUTOMOTIVE': '🚗',
      'MANUFACTURING': '🏭'
    };
    return iconMap[categoryId] || '💬';
  };

  // Dynamic color mapping
  const getCategoryColor = (categoryId: string) => {
    const colorMap: {[key: string]: string} = {
      'ECOMMERCE': 'from-blue-500 to-cyan-600',
      'REAL_ESTATE': 'from-green-500 to-emerald-600',
      'ERP': 'from-purple-500 to-violet-600',
      'IP_REGISTRATION': 'from-indigo-500 to-blue-600',
      'HEALTHCARE': 'from-red-500 to-pink-600',
      'FINANCE': 'from-yellow-500 to-orange-600',
      'EDUCATION': 'from-teal-500 to-cyan-600',
      'LEGAL': 'from-gray-500 to-slate-600',
      'TRAVEL': 'from-sky-500 to-blue-600',
      'FOOD': 'from-orange-500 to-red-600',
      'TECH': 'from-violet-500 to-purple-600',
      'RETAIL': 'from-pink-500 to-rose-600'
    };
    return colorMap[categoryId] || 'from-gray-500 to-slate-600';
  };

  // Form data for modals
  const [formData, setFormData] = useState<CreateScriptData>({
    name: "",
    websiteUrl: "",
    selectedCategory: "",
    customization: {
      position: "bottom-right",
      theme: "light",
      primaryColor: "#007bff",
      welcomeMessage: "",
      title: "",
      placeholder: "Type your message...",
      maxWidth: 380,
      maxHeight: 600,
      autoOpen: false,
      showOnMobile: true,
    },
  });

  const resetFormData = () => {
    const defaultCategory = availableCategories.length > 0 ? availableCategories[0] : null;
    setFormData({
      name: "",
      websiteUrl: "",
      selectedCategory: defaultCategory?.category_id || "",
      customization: {
        position: "bottom-right",
        theme: "light",
        primaryColor: "#007bff",
        welcomeMessage: defaultCategory?.defaultWelcome || "Hi! How can I help you today?",
        title: "",
        placeholder: defaultCategory?.placeholder || "Type your message...",
        maxWidth: 380,
        maxHeight: 600,
        autoOpen: false,
        showOnMobile: true,
      },
    });
  };

  // Update form data when category selection changes
  const handleCategoryChange = (categoryId: string) => {
    const selectedCategory = getSelectedCategoryConfig(categoryId);
    setFormData(prev => ({
      ...prev,
      selectedCategory: categoryId,
      customization: {
        ...prev.customization,
        welcomeMessage: selectedCategory.defaultWelcome,
        placeholder: selectedCategory.placeholder,
      }
    }));
  };

  useEffect(() => {
    loadUserInfo();
    loadAvailableCategories();
    loadScripts();
  }, []);

  // Update form data when available categories load
  useEffect(() => {
    if (availableCategories.length > 0 && !formData.selectedCategory) {
      resetFormData();
    }
  }, [availableCategories]);

  // Load available categories from backend
  const loadAvailableCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/scripts/available-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setAvailableCategories(data.categories);
      }
    } catch (error) {
      console.error("Failed to load available categories:", error);
      setAvailableCategories([{
        category_id: "GENERAL",
        category_name: "General",
        description: "General purpose AI assistant",
        defaultWelcome: "Hi! How can I help you today?",
        placeholder: "Type your message...",
        allowedTopics: [],
        tone: "professional-friendly",
        style: "helpful-general"
      }]);
    }
  };

  const loadUserInfo = () => {
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsedUser = JSON.parse(user);
        setUserInfo(parsedUser);
      }
    } catch (error) {
      console.error("Failed to load user info:", error);
    }
  };

  const loadScripts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/scripts`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setScripts(data.scripts);
      }
    } catch (error) {
      console.error("Failed to load scripts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScript = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading({ type: "create", id: "modal" });
    try {
      const token = localStorage.getItem("token");
      const updatedFormData = {
        ...formData,
        customization: {
          ...formData.customization,
          title: formData.name,
        },
      };
      const response = await fetch(`${API_BASE_URL}/api/scripts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedFormData),
      });

      const data = await response.json();
      if (data.success) {
        setScripts([data.script, ...scripts]);
        setShowCreateModal(false);
        resetFormData();
      } else {
        alert(data.message || "Failed to create script");
      }
    } catch (error) {
      console.error("Failed to create script:", error);
      alert("Failed to create script");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditScript = (script: Script) => {
    const categoryConfig = getSelectedCategoryConfig(script.industryCategory || userInfo.category);
    setSelectedScript(script);
    setFormData({
      name: script.name,
      websiteUrl: script.websiteUrl,
      selectedCategory: script.industryCategory || userInfo.category || "",
      customization: {
        position: script.customization?.position || "bottom-right",
        theme: script.customization?.theme || "light",
        primaryColor: script.customization?.primaryColor || "#007bff",
        welcomeMessage: script.customization?.welcomeMessage || categoryConfig.defaultWelcome,
        title: script.customization?.title || script.name,
        placeholder: script.customization?.placeholder || categoryConfig.placeholder,
        maxWidth: script.customization?.maxWidth || 380,
        maxHeight: script.customization?.maxHeight || 600,
        autoOpen: script.customization?.autoOpen || false,
        showOnMobile: script.customization?.showOnMobile || true,
      },
    });
    setShowEditModal(true);
  };

  const handleUpdateScript = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScript) return;

    setActionLoading({ type: "update", id: selectedScript.scriptId });
    try {
      const token = localStorage.getItem("token");
      const updatedFormData = {
        ...formData,
        customization: {
          ...formData.customization,
          title: formData.name,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/scripts/${selectedScript.scriptId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedFormData),
        }
      );

      const data = await response.json();
      if (data.success) {
        setScripts(
          scripts.map((script) =>
            script.scriptId === selectedScript.scriptId
              ? { ...script, ...data.script }
              : script
          )
        );
        setShowEditModal(false);
        setSelectedScript(null);
        resetFormData();
      } else {
        alert(data.message || "Failed to update script");
      }
    } catch (error) {
      console.error("Failed to update script:", error);
      alert("Failed to update script");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteScript = async () => {
    if (!selectedScript) return;

    setActionLoading({ type: "delete", id: selectedScript.scriptId });
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/scripts/${selectedScript.scriptId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setScripts(
          scripts.filter(
            (script) => script.scriptId !== selectedScript.scriptId
          )
        );
        setShowDeleteModal(false);
        setSelectedScript(null);
      } else {
        alert(data.message || "Failed to delete script");
      }
    } catch (error) {
      console.error("Failed to delete script:", error);
      alert("Failed to delete script");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleScript = async (scriptId: string) => {
    setActionLoading({ type: "toggle", id: scriptId });
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/scripts/${scriptId}/toggle`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setScripts(
          scripts.map((script) =>
            script.scriptId === scriptId
              ? { ...script, isActive: data.script.isActive }
              : script
          )
        );
      } else {
        alert(data.message || "Failed to toggle script");
      }
    } catch (error) {
      console.error("Failed to toggle script:", error);
      alert("Failed to toggle script");
    } finally {
      setActionLoading(null);
    }
  };

  const handleShowCode = async (scriptId: string) => {
    setActionLoading({ type: "code", id: scriptId });
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/scripts/${scriptId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setSelectedScript(data.script);
        setShowCodeModal(true);
      } else {
        alert(data.message || "Failed to get script code");
      }
    } catch (error) {
      console.error("Failed to get script code:", error);
      alert("Failed to get script code");
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPositionIcon = (position: string) => {
    switch (position) {
      case "bottom-right":
        return "↘️";
      case "bottom-left":
        return "↙️";
      case "top-right":
        return "↗️";
      case "top-left":
        return "↖️";
      default:
        return "📍";
    }
  };

  // Helper functions
  const getCategoryDescription = (categoryId: string) => {
    const category = availableCategories.find(cat => cat.category_id === categoryId);
    return category?.description || "AI-powered chat assistant";
  };

  const getCategoryAllowedTopics = (categoryId: string) => {
    const category = availableCategories.find(cat => cat.category_id === categoryId);
    return category?.allowedTopics || [];
  };

  const getCategoryTone = (categoryId: string) => {
    const category = availableCategories.find(cat => cat.category_id === categoryId);
    return category?.tone || "professional-friendly";
  };

  const getCategoryStyle = (categoryId: string) => {
    const category = availableCategories.find(cat => cat.category_id === categoryId);
    return category?.style || "helpful-general";
  };

  const currentCategoryConfig = getSelectedCategoryConfig(formData.selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full mx-auto mb-4 animate-pulse flex items-center justify-center">
            <span className="text-2xl">⚡</span>
          </div>
          <div className="text-gray-600 font-medium">
            Loading your scripts...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="bg-white/80 backdrop-blur-lg shadow-xl border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl">⚡</span>
              </div>
              <div>
                <Link
                  to="/"
                  className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium mb-1 hover:underline"
                >
                  <span className="mr-1">🏠</span>
                  Dashboard
                </Link>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  ⚡ AI-Powered Scripts
                </h1>
                <p className="text-gray-600 text-sm">
                  Create intelligent chat widgets with industry-specific AI
                </p>
              </div>
            </div>

            {scripts.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium transform hover:-translate-y-0.5"
              >
                <span className="mr-2">✨</span>
                Create New Script
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {scripts.length === 0 ? (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto bg-white/70 backdrop-blur-lg rounded-3xl shadow-xl p-8 border border-white/20">
                <div className="w-20 h-20 bg-gradient-to-r from-gray-500 to-slate-600 rounded-3xl mx-auto mb-6 flex items-center justify-center shadow-lg">
                  <span className="text-3xl">💬</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Ready to Get Started?
                </h3>
                <p className="text-gray-600 mb-6">
                  Create your first AI-powered chat script and start engaging with your website visitors instantly.
                </p>
                
                {/* Show available categories */}
                {availableCategories.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-3">Available AI Categories:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {availableCategories.map((category) => (
                        <span
                          key={category.category_id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {getCategoryIcon(category.category_id)} {category.category_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-2xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium transform hover:-translate-y-0.5"
                >
                  <span className="mr-2">✨</span>
                  Create Your First Script
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/20">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-white text-lg">📊</span>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {scripts.length}
                      </p>
                      <p className="text-sm text-gray-600">Total Scripts</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/20">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-white text-lg">✅</span>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {scripts.filter((s) => s.isActive).length}
                      </p>
                      <p className="text-sm text-gray-600">Active Scripts</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/20">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-white text-lg">💬</span>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {scripts.reduce((sum, s) => sum + s.usageCount, 0)}
                      </p>
                      <p className="text-sm text-gray-600">Total Sessions</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/20">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-white text-lg">🌐</span>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {new Set(scripts.map((s) => s.websiteUrl)).size}
                      </p>
                      <p className="text-sm text-gray-600">Websites</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add Script Button for existing scripts */}
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">Your AI Scripts</h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-2xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium transform hover:-translate-y-0.5"
                >
                  <span className="mr-2">✨</span>
                  Create New Script
                </button>
              </div>

              {/* Scripts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {scripts.map((script) => {
                  const scriptCategoryConfig = getSelectedCategoryConfig(script.industryCategory || userInfo.category);
                  return (
                    <div
                      key={script.id}
                      className="group bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className={`bg-gradient-to-r ${getCategoryColor(scriptCategoryConfig.category_id)} p-6 text-white`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                              <span className="text-lg">
                                {getPositionIcon(script.customization?.position)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-lg truncate flex items-center">
                                {getCategoryIcon(scriptCategoryConfig.category_id)} {script.name}
                              </h3>
                              <p className="text-white/90 text-sm truncate">
                                {script.websiteUrl}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              script.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {script.isActive ? "🟢 Active" : "🔴 Inactive"}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="text-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                            <div className="text-2xl font-bold text-blue-600">
                              {script.usageCount}
                            </div>
                            <div className="text-xs text-blue-500 font-medium">
                              Sessions
                            </div>
                          </div>
                          {/* <div className="text-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                            <div className="text-sm font-bold text-green-600">
                              {script.customization?.theme === "light" ? "☀️ Light" : "🌙 Dark"}
                            </div>
                            <div className="text-xs text-green-500 font-medium">
                              Theme
                            </div>
                          </div> */}
                        </div>

                        {/* Category Context */}
                        <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                          <p className="text-sm text-purple-600 font-medium mb-1 flex items-center">
                            {getCategoryIcon(scriptCategoryConfig.category_id)}
                            <span className="ml-1">
                              Category: {scriptCategoryConfig.category_name}
                            </span>
                          </p>
                          <p className="text-xs text-purple-500">
                            AI optimized for {scriptCategoryConfig.category_name.toLowerCase()} interactions
                          </p>
                        </div>

                        <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                          <p className="text-sm text-gray-600 font-medium mb-1">
                            Welcome Message:
                          </p>
                          <p className="text-xs text-gray-800 italic">
                            "{script.customization?.welcomeMessage}"
                          </p>
                        </div>

                        <div className="text-xs text-gray-500 mb-4">
                          Created: {formatDate(script.createdAt)}
                        </div>
                      </div>

                      <div className="bg-gray-50/50 p-4 space-y-2">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleShowCode(script.scriptId)}
                            disabled={
                              actionLoading?.type === "code" &&
                              actionLoading?.id === script.scriptId
                            }
                            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center"
                          >
                            {actionLoading?.type === "code" &&
                            actionLoading?.id === script.scriptId ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <>
                                <span className="mr-1">📋</span>
                                Get Code
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleToggleScript(script.scriptId)}
                            disabled={
                              actionLoading?.type === "toggle" &&
                              actionLoading?.id === script.scriptId
                            }
                            className={`flex-1 px-4 py-2 rounded-xl transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center ${
                              script.isActive
                                ? "bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700"
                                : "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                            }`}
                          >
                            {actionLoading?.type === "toggle" &&
                            actionLoading?.id === script.scriptId ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <>
                                <span className="mr-1">
                                  {script.isActive ? "⏸️" : "▶️"}
                                </span>
                                {script.isActive ? "Disable" : "Enable"}
                              </>
                            )}
                          </button>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditScript(script)}
                            disabled={
                              actionLoading?.type === "edit" &&
                              actionLoading?.id === script.scriptId
                            }
                            className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-4 py-2 rounded-xl hover:from-blue-600 hover:to-cyan-700 transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center"
                          >
                            <span className="mr-1">✏️</span>
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setSelectedScript(script);
                              setShowDeleteModal(true);
                            }}
                            disabled={
                              actionLoading?.type === "delete" &&
                              actionLoading?.id === script.scriptId
                            }
                            className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 py-2 rounded-xl hover:from-red-600 hover:to-rose-700 transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center"
                          >
                            <span className="mr-1">🗑️</span>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal with Category Dropdown */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div
                  className={`w-10 h-10 bg-gradient-to-r ${getCategoryColor(currentCategoryConfig.category_id)} rounded-xl flex items-center justify-center`}
                >
                  <span className="text-white text-lg">
                    {getCategoryIcon(currentCategoryConfig.category_id)}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    Create {currentCategoryConfig.category_name} Script
                  </h3>
                  <p className="text-sm text-gray-600">
                    {currentCategoryConfig.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetFormData();
                }}
                disabled={actionLoading?.type === "create"}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
              >
                <span className="text-gray-600">✕</span>
              </button>
            </div>

            <form onSubmit={handleCreateScript} className="space-y-6">
              {/* Category Selection Dropdown */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="mr-1">🤖</span>
                  AI Category Selection
                </label>
                <select
                  value={formData.selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full border-2 border-blue-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white"
                  disabled={actionLoading?.type === "create"}
                  required
                >
                  <option value="">Select an AI Category</option>
                  {availableCategories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {getCategoryIcon(category.category_id)} {category.category_name}
                    </option>
                  ))}
                </select>
                {formData.selectedCategory && (
                  <div className="mt-2 p-3 bg-white/60 rounded-lg">
                    <p className="text-xs text-blue-700 font-medium">
                      ✨ Selected: {currentCategoryConfig.category_name}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {currentCategoryConfig.description}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="mr-1">📝</span>
                    Script Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    placeholder={`My ${currentCategoryConfig.category_name} Chat`}
                    disabled={actionLoading?.type === "create"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="mr-1">🌐</span>
                    Website URL
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        websiteUrl: e.target.value,
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    placeholder="https://mywebsite.com"
                    disabled={actionLoading?.type === "create"}
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="mr-2">🎨</span>
                  Customization Options
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position
                    </label>
                    <select
                      value={formData.customization.position}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            position: e.target.value,
                          },
                        }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={actionLoading?.type === "create"}
                    >
                      <option value="bottom-right">↘️ Bottom Right</option>
                      <option value="bottom-left">↙️ Bottom Left</option>
                      <option value="top-right">↗️ Top Right</option>
                      <option value="top-left">↖️ Top Left</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.customization.primaryColor}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            customization: {
                              ...prev.customization,
                              primaryColor: e.target.value,
                            },
                          }))
                        }
                        className="w-16 h-12 border-2 border-gray-200 rounded-xl cursor-pointer"
                        disabled={actionLoading?.type === "create"}
                      />
                      <div className="text-sm text-gray-600">
                        {formData.customization.primaryColor}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getCategoryIcon(currentCategoryConfig.category_id)} Welcome Message ({currentCategoryConfig.category_name} Optimized)
                  </label>
                  <input
                    type="text"
                    value={formData.customization.welcomeMessage}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          welcomeMessage: e.target.value,
                        },
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={currentCategoryConfig.defaultWelcome}
                    disabled={actionLoading?.type === "create"}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This message is optimized for {currentCategoryConfig.category_name.toLowerCase()} interactions
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Input Placeholder
                  </label>
                  <input
                    type="text"
                    value={formData.customization.placeholder}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          placeholder: e.target.value,
                        },
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={currentCategoryConfig.placeholder}
                    disabled={actionLoading?.type === "create"}
                  />
                </div>

                <div className="mt-4 flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="autoOpen"
                    checked={formData.customization.autoOpen}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          autoOpen: e.target.checked,
                        },
                      }))
                    }
                    className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500"
                    disabled={actionLoading?.type === "create"}
                  />
                  <label
                    htmlFor="autoOpen"
                    className="text-sm text-gray-700 font-medium"
                  >
                    🚀 Auto-open chat widget for immediate {currentCategoryConfig.category_name.toLowerCase()} assistance
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetFormData();
                  }}
                  disabled={actionLoading?.type === "create"}
                  className="px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading?.type === "create" || !formData.selectedCategory}
                  className={`px-8 py-3 bg-gradient-to-r ${getCategoryColor(currentCategoryConfig.category_id)} text-white rounded-xl hover:shadow-xl font-medium transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none flex items-center space-x-2 transition-all duration-200`}
                >
                  {actionLoading?.type === "create" && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>{getCategoryIcon(currentCategoryConfig.category_id)}</span>
                  <span>
                    {actionLoading?.type === "create"
                      ? "Creating..."
                      : `Create ${currentCategoryConfig.category_name} Script`}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div
                  className={`w-10 h-10 bg-gradient-to-r ${getCategoryColor(currentCategoryConfig.category_id)} rounded-xl flex items-center justify-center`}
                >
                  <span className="text-white text-lg">✏️</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    Edit {currentCategoryConfig.category_name} Script
                  </h3>
                  <p className="text-sm text-gray-600">
                    Modify your {currentCategoryConfig.category_name.toLowerCase()} chat widget
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedScript(null);
                }}
                disabled={actionLoading?.type === "update"}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
              >
                <span className="text-gray-600">✕</span>
              </button>
            </div>

            <form onSubmit={handleUpdateScript} className="space-y-6">
              {/* Category Selection (Read-only in edit mode) */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="mr-1">🤖</span>
                  AI Category (Current)
                </label>
                <div className="w-full border-2 border-blue-200 rounded-xl px-4 py-3 bg-gray-100">
                  <div className="flex items-center space-x-2">
                    <span>{getCategoryIcon(currentCategoryConfig.category_id)}</span>
                    <span className="font-medium">{currentCategoryConfig.category_name}</span>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Category cannot be changed after creation. Create a new script to use a different AI category.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="mr-1">📝</span>
                    Script Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    placeholder={`My ${currentCategoryConfig.category_name} Chat`}
                    disabled={actionLoading?.type === "update"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="mr-1">🌐</span>
                    Website URL
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        websiteUrl: e.target.value,
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    placeholder="https://mywebsite.com"
                    disabled={actionLoading?.type === "update"}
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="mr-2">🎨</span>
                  Customization Options
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position
                    </label>
                    <select
                      value={formData.customization.position}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            position: e.target.value,
                          },
                        }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={actionLoading?.type === "update"}
                    >
                      <option value="bottom-right">↘️ Bottom Right</option>
                      <option value="bottom-left">↙️ Bottom Left</option>
                      <option value="top-right">↗️ Top Right</option>
                      <option value="top-left">↖️ Top Left</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.customization.primaryColor}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            customization: {
                              ...prev.customization,
                              primaryColor: e.target.value,
                            },
                          }))
                        }
                        className="w-16 h-12 border-2 border-gray-200 rounded-xl cursor-pointer"
                        disabled={actionLoading?.type === "update"}
                      />
                      <div className="text-sm text-gray-600">
                        {formData.customization.primaryColor}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {getCategoryIcon(currentCategoryConfig.category_id)} Welcome Message ({currentCategoryConfig.category_name} AI)
                  </label>
                  <input
                    type="text"
                    value={formData.customization.welcomeMessage}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          welcomeMessage: e.target.value,
                        },
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={currentCategoryConfig.defaultWelcome}
                    disabled={actionLoading?.type === "update"}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This message is AI-optimized for {currentCategoryConfig.category_name.toLowerCase()} interactions
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Input Placeholder
                  </label>
                  <input
                    type="text"
                    value={formData.customization.placeholder}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          placeholder: e.target.value,
                        },
                      }))
                    }
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={currentCategoryConfig.placeholder}
                    disabled={actionLoading?.type === "update"}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Theme
                    </label>
                    <select
                      value={formData.customization.theme}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            theme: e.target.value,
                          },
                        }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={actionLoading?.type === "update"}
                    >
                      <option value="light">☀️ Light</option>
                      <option value="dark">🌙 Dark</option>
                    </select>
                  </div> */}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="autoOpenEdit"
                      checked={formData.customization.autoOpen}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            autoOpen: e.target.checked,
                          },
                        }))
                      }
                      className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500 mr-3"
                      disabled={actionLoading?.type === "update"}
                    />
                    <label
                      htmlFor="autoOpenEdit"
                      className="text-sm text-gray-700 font-medium"
                    >
                      🚀 Auto-open chat widget
                    </label>
                  </div>
                </div>

                {/* <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Width (px)
                    </label>
                    <input
                      type="number"
                      min="300"
                      max="600"
                      value={formData.customization.maxWidth}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            maxWidth: parseInt(e.target.value) || 380,
                          },
                        }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={actionLoading?.type === "update"}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Height (px)
                    </label>
                    <input
                      type="number"
                      min="400"
                      max="800"
                      value={formData.customization.maxHeight}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            maxHeight: parseInt(e.target.value) || 600,
                          },
                        }))
                      }
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={actionLoading?.type === "update"}
                    />
                  </div>
                </div> */}

                {/* <div className="mt-4 flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="showOnMobileEdit"
                    checked={formData.customization.showOnMobile}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customization: {
                          ...prev.customization,
                          showOnMobile: e.target.checked,
                        },
                      }))
                    }
                    className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500"
                    disabled={actionLoading?.type === "update"}
                  />
                  <label
                    htmlFor="showOnMobileEdit"
                    className="text-sm text-gray-700 font-medium"
                  >
                    📱 Show on mobile devices
                  </label>
                </div> */}
              </div>
              
              <div className="flex justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedScript(null);
                  }}
                  disabled={actionLoading?.type === "update"}
                  className="px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading?.type === "update"}
                  className={`px-8 py-3 bg-gradient-to-r ${getCategoryColor(currentCategoryConfig.category_id)} text-white rounded-xl hover:shadow-xl font-medium transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none flex items-center space-x-2 transition-all duration-200`}
                >
                  {actionLoading?.type === "update" && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {actionLoading?.type === "update" ? "Updating..." : "Update Script"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedScript && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-3xl">🗑️</span>
              </div>

              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Delete Script
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{selectedScript.name}</strong>"? 
                This action cannot be undone and will break the widget on your website.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <span className="text-red-500 text-xl">⚠️</span>
                  <div className="text-left">
                    <p className="text-sm text-red-700 font-medium">Warning:</p>
                    <ul className="text-xs text-red-600 mt-1 space-y-1">
                      <li>• Widget will stop working immediately</li>
                      <li>• All chat history will be preserved</li>
                      <li>• Script code cannot be recovered</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedScript(null);
                  }}
                  disabled={actionLoading?.type === "delete"}
                  className="flex-1 px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteScript}
                  disabled={actionLoading?.type === "delete"}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {actionLoading?.type === "delete" && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {actionLoading?.type === "delete" ? "Deleting..." : "Delete Script"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Modal */}
      {showCodeModal && selectedScript && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
            {(() => {
              const scriptCategoryConfig = getSelectedCategoryConfig(selectedScript.industryCategory || userInfo.category);
              return (
                <>
                  <div className={`bg-gradient-to-r ${getCategoryColor(scriptCategoryConfig.category_id)} p-6 text-white`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">
                            {getCategoryIcon(scriptCategoryConfig.category_id)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold">
                            {scriptCategoryConfig.category_name} Widget Code Ready!
                          </h3>
                          <p className="text-white/90">
                            For "{selectedScript.name}" - AI optimized
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowCodeModal(false)}
                        className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-colors"
                      >
                        <span className="text-white text-xl">✕</span>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                    <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
                      <h4 className="font-bold text-blue-900 mb-4 flex items-center text-lg">
                        <span className="mr-2">{getCategoryIcon(scriptCategoryConfig.category_id)}</span>
                        {scriptCategoryConfig.category_name} Widget Installation Guide
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                            1
                          </div>
                          <div>
                            <p className="font-semibold text-blue-900">Copy the Code</p>
                            <p className="text-sm text-blue-700">AI-optimized widget code</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                            2
                          </div>
                          <div>
                            <p className="font-semibold text-blue-900">Paste in HTML</p>
                            <p className="text-sm text-blue-700">Before closing &lt;/body&gt; tag</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                            3
                          </div>
                          <div>
                            <p className="font-semibold text-blue-900">Go Live!</p>
                            <p className="text-sm text-blue-700">{scriptCategoryConfig.category_name} AI ready</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-100 rounded-xl">
                        <p className="text-sm text-blue-800 font-medium flex items-center">
                          <span className="mr-2">{getCategoryIcon(scriptCategoryConfig.category_id)}</span>
                          This widget is AI-optimized for {scriptCategoryConfig.category_name.toLowerCase()} interactions.
                        </p>
                      </div>
                    </div>

                    <div className="relative group">
                      <div className="absolute top-4 right-4 z-10">
                        <button
                          onClick={() => copyToClipboard(selectedScript.scriptCode || "")}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                            copySuccess
                              ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                              : `bg-gradient-to-r ${getCategoryColor(scriptCategoryConfig.category_id)} text-white hover:shadow-2xl`
                          }`}
                        >
                          <span className="mr-1">
                            {copySuccess ? "✅" : getCategoryIcon(scriptCategoryConfig.category_id)}
                          </span>
                          {copySuccess ? "Copied!" : "Copy Code"}
                        </button>
                      </div>
                      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-600">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="ml-4 text-gray-300 text-sm font-mono">
                              {scriptCategoryConfig.category_name.toLowerCase()}-widget.js
                            </span>
                          </div>
                        </div>
                        <pre className="p-6 text-gray-100 text-sm font-mono overflow-x-auto leading-relaxed max-h-96">
                          <code>{selectedScript.scriptCode}</code>
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>{getCategoryIcon(scriptCategoryConfig.category_id)}</span>
                      <span>
                        Deploy to: <strong>{selectedScript.websiteUrl}</strong>
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                        {scriptCategoryConfig.category_name} AI Optimized
                      </span>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => copyToClipboard(selectedScript.scriptCode || "")}
                        className={`px-6 py-2 rounded-xl text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                          copySuccess
                            ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                            : `bg-gradient-to-r ${getCategoryColor(scriptCategoryConfig.category_id)} text-white`
                        }`}
                      >
                        <span className="mr-1">
                          {copySuccess ? "✅" : getCategoryIcon(scriptCategoryConfig.category_id)}
                        </span>
                        {copySuccess ? "Copied!" : "Copy Code"}
                      </button>
                      <button
                        onClick={() => setShowCodeModal(false)}
                        className="px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScriptGenerator;